import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { metaPixel } from '@/utils/meta-pixel';
import { STRIPE_PRODUCTS, getProductById } from '@/stripe-config';

export const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const creditsParam = searchParams.get('credits');
  const creditsAmount = creditsParam ? parseInt(creditsParam, 10) : null;
  const purchaseType = searchParams.get('type');

  const isCreditPurchase = purchaseType === 'credit';
  const isSubscriptionPurchase = purchaseType === 'subscription';

  React.useEffect(() => {
    let purchaseAmount = 0;
    let pType: 'credit' | 'subscription' = 'credit';

    if (isCreditPurchase && creditsAmount) {
      const creditProduct = getProductById('one_time_credit');
      purchaseAmount = creditProduct ? creditProduct.price * creditsAmount : 9.90 * creditsAmount;
      pType = 'credit';
    } else {
      const subscriptionProduct = STRIPE_PRODUCTS.find(p => p.mode === 'subscription' && !p.isLegacy);
      purchaseAmount = subscriptionProduct?.price || 19.90;
      pType = 'subscription';
    }

    metaPixel.trackPurchaseComplete(purchaseAmount, pType, 'EUR');
  }, [isCreditPurchase, isSubscriptionPurchase, creditsAmount]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 3000);
    return () => clearTimeout(timer);
  }, [navigate]);

  const handleGoToDashboard = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth?mode=login&redirect=/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Helmet>
        <title>Paiement réussi - DPEWizard</title>
        <meta name="description" content="Votre paiement a été traité avec succès. Commencez immédiatement votre analyse DPE." />
      </Helmet>

      <div className="max-w-lg w-full">
        <Card className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-500 to-blue-500 text-white text-center py-8">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold mb-2">
              Votre analyse DPE est prête !
            </CardTitle>
            <CardDescription className="text-white/90 text-lg">
              {isAuthenticated
                ? (isCreditPurchase
                    ? `${creditsAmount} crédit${creditsAmount && creditsAmount > 1 ? 's' : ''} ajouté${creditsAmount && creditsAmount > 1 ? 's' : ''} avec succès à votre compte`
                    : 'Abonnement illimité activé avec succès')
                : (isCreditPurchase
                    ? `Compte créé automatiquement et ${creditsAmount} crédit${creditsAmount && creditsAmount > 1 ? 's' : ''} d'analyse ajouté${creditsAmount && creditsAmount > 1 ? 's' : ''}`
                    : 'Compte créé automatiquement et abonnement illimité activé')}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-8">
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">Paiement confirmé</p>
                  <p className="text-sm text-green-600">Transaction sécurisée via Stripe</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                Accéder au tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuccessPage;
